import React from "react";
import { Trans } from "@lingui/macro";
import { TransProps } from "@lingui/react";
import { AvailableRoles, RolesWithOutAccess } from "../../constants/roles";
import {
  USERPOSITIONS,
  POSITION_WITHOUT_ACCESS,
} from "../../constants/userPositions";

export type typeNotificationMessage = {
  title: React.ReactElement<TransProps>;
  message: React.ReactElement<TransProps>;
  type: "warning" | "success" | "error" | "info";
};

export const NOTIFICATION_MESSAGES: { [key: string]: typeNotificationMessage } =
  {
    ERROR_SERVER_CONNECTION: {
      type: "error",
      title: <Trans>Error</Trans>,
      message: <Trans>Server connection error.</Trans>,
    },
    ERROR_CATALOG_CONNECTION: {
      type: "error",
      title: <Trans>Error</Trans>,
      message: <Trans>Product's catalog connection error.</Trans>,
    },

    ERROR_INVALID_PSID: {
      type: "error",
      title: <Trans>Error</Trans>,
      message: <Trans>Product has invalid PSID. Forbidden for sale.</Trans>,
    },

    SUCCESS_ADD_ITEM: {
      type: "success",
      title: <Trans>Success</Trans>,
      message: <Trans>Item added successfully.</Trans>,
    },
    SUCCESS_CHANGE_ITEM: {
      type: "success",
      title: <Trans>Success</Trans>,
      message: <Trans>Item changed successfully.</Trans>,
    },
    SUCCESS_DELETE_ITEM: {
      type: "success",
      title: <Trans>Success</Trans>,
      message: <Trans>Item deleted successfully.</Trans>,
    },
    SUCCESS_DELETE_ITEMS: {
      type: "success",
      title: <Trans>Success</Trans>,
      message: <Trans>Items deleted successfully.</Trans>,
    },
    SUCCESS_RECOVER_ITEM: {
      type: "success",
      title: <Trans>Success</Trans>,
      message: <Trans>Item recovered successfully.</Trans>,
    },

    SUCCESS_ADD_CALLBACK: {
      type: "success",
      title: <Trans>Success</Trans>,
      message: <Trans>Callback added successfully.</Trans>,
    },
    SUCCESS_DELETE_CALLBACK: {
      type: "success",
      title: <Trans>Success</Trans>,
      message: <Trans>Callback deleted successfully.</Trans>,
    },
    SUCCESS_EDIT_URL_CALLBACK: {
      type: "success",
      title: <Trans>Success</Trans>,
      message: <Trans>Callback edited successfully.</Trans>,
    },

    SUCCESS_BULK_EDIT: {
      type: "success",
      title: <Trans>Success</Trans>,
      message: <Trans>Items changed successfully.</Trans>,
    },
    SUCCESS_BULK_DELETE: {
      type: "success",
      title: <Trans>Success</Trans>,
      message: <Trans>Items deleted successfully.</Trans>,
    },

    SUCCESS_IMPORT: {
      type: "success",
      title: <Trans>Success</Trans>,
      message: <Trans>Import data successfully.</Trans>,
    },

    INFO_PRODUCT_ALREADY_EXIST: {
      type: "info",
      title: <Trans>Info</Trans>,
      message: (
        <Trans>
          Product has already been added. Please correct the product amount.
        </Trans>
      ),
    },
    INFO_PRODUCT_MARKING_ALREADY_EXIST: {
      type: "info",
      title: <Trans>Info</Trans>,
      message: <Trans>Product with this marking already added.</Trans>,
    },

    ERROR_SERVER_BAD_IMPORT_FILE: {
      type: "error",
      title: <Trans>Error</Trans>,
      message: (
        <Trans>
          Import is not possible because the file has an incorrect structure or
          contains errors.
        </Trans>
      ),
    },

    ERROR_SERVER_BAD_IMPORT_FILE_FIELDS: {
      type: "error",
      title: <Trans>Error</Trans>,
      message: (
        <Trans>The number of fields in the file must be equal to 15</Trans>
      ),
    },

    ERROR_STATISTIC: {
      type: "error",
      title: <Trans>Error</Trans>,
      message: <Trans>Can't to get data.</Trans>,
    },

    ERROR_EDIT_STORE_UNKNOWN: {
      type: "error",
      title: <Trans>Error</Trans>,
      message: <Trans>Unknown error editing store.</Trans>,
    },
    ERROR_EDIT_STORE_FIELD_VALIDATION: {
      type: "error",
      title: <Trans>Error</Trans>,
      message: <Trans>Some fields are incorrect.</Trans>,
    },
    ERROR_EDIT_STORE_UNKNOWN_FIELD_VALIDATION: {
      type: "error",
      title: <Trans>Error</Trans>,
      message: <Trans>Unknown validation error.</Trans>,
    },

    ERROR_ACCESS: {
      type: "error",
      title: <Trans>Access error</Trans>,
      message: <Trans>You don't have access to change this.</Trans>,
    },
    ERROR_ORDER_CANNOT_BE_CHANGED: {
      type: "error",
      title: <Trans>Error</Trans>,
      message: (
        <Trans>The order cannot be changed. Please refresh the page.</Trans>
      ),
    },

    SUCCESS_CONNECT_ITEM: {
      type: "success",
      title: <Trans>Success</Trans>,
      message: <Trans>Item connected successfully.</Trans>,
    },
    ERROR_CONNECT_ITEM: {
      type: "error",
      title: <Trans>Error</Trans>,
      message: <Trans>Connection finished with error.</Trans>,
    },

    SUCCESS_DISCONNECT_ITEM: {
      type: "success",
      title: <Trans>Success</Trans>,
      message: <Trans>Item disconnected successfully.</Trans>,
    },
    ERROR_DISCONNECT_ITEM: {
      type: "error",
      title: <Trans>Error</Trans>,
      message: <Trans>Disconnection finished with error.</Trans>,
    },
    PRODUCT_PACKAGE_CODE_REQUIRED: {
      type: "error",
      title: <Trans>Error</Trans>,
      message: <Trans>The packaging code cannot be absent or empty.</Trans>,
    },

    SUCCESS_EXPORT: {
      type: "success",
      title: <Trans>Success</Trans>,
      message: (
        <Trans>
          Your export is being prepared. A download link will be sent to your
          email
        </Trans>
      ),
    },
  };

export type typePagination = {
  pageNumber: number;
  pageCount?: number;
  totalItems?: number;
  isLastPage?: boolean;
  isFirstPage?: boolean;
  itemsPerPage?: number;
};

export type typeRequestPagination = {
  page: number;
  size: number;
};

// Pagination mapper
export const paginationMapper = (paginationObject: any): typePagination => {
  return {
    pageNumber: paginationObject.number,
    pageCount: paginationObject.totalPages,
    totalItems: paginationObject.totalElements,
    isFirstPage: paginationObject.first,
    isLastPage: paginationObject.last,
  };
};

export const phonePattern = "+998 (__) ___-__-__";

export const startNumber = "+" + phonePattern!.replace(/\D/g, "");

export const checkAccess = (
  role: AvailableRoles | null,
  position: USERPOSITIONS,
): boolean => {
  return (!!role &&
    !RolesWithOutAccess.includes(role) &&
    !!position &&
    !POSITION_WITHOUT_ACCESS.includes(position)) as boolean;
};
